import React from 'react';
import { Calendar, MapPin, TrendingUp } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import portfolioData from '../data/mock';

const Experience = () => {
  return (
    <section id="experience" className="py-24 bg-stone-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-stone-100 mb-6">
            Experiencia Profesional
          </h2>
          <p className="text-xl text-stone-400 max-w-3xl mx-auto">
            Más de tres décadas transformando la agricultura a través de innovación, liderazgo y tecnología
          </p>
        </div>

        <div className="space-y-8">
          {portfolioData.experience.map((job, index) => {
            // Map background images for each company
            const getBackgroundImage = (company) => {
              switch(company) {
                case 'Clase Azul México':
                  return portfolioData.images.claseAzulBg;
                case 'Grupo Modelo':
                  return portfolioData.images.grupoModeloBg;
                case 'Servicios de Gestión Integral':
                  return portfolioData.images.consultorBg;
                case 'Monsanto':
                  return portfolioData.images.monsantoBg;
                default:
                  return null;
              }
            };

            const backgroundImage = getBackgroundImage(job.company);

            return (
              <Card key={job.id} className="bg-stone-800/50 border-stone-700 hover:bg-stone-800/70 transition-all duration-300 overflow-hidden relative">
                {/* Background Image as Watermark */}
                {backgroundImage && (
                  <div className="absolute inset-0 z-0">
                    <img 
                      src={backgroundImage}
                      alt={`Background for ${job.company}`}
                      className="w-full h-full object-cover opacity-10 grayscale"
                    />
                    <div className="absolute inset-0 bg-stone-900/60"></div>
                  </div>
                )}

                <div className="relative z-10 p-8">
                  <div className="grid lg:grid-cols-3 gap-8">
                    {/* Job Info */}
                    <div className="lg:col-span-1 space-y-4">
                      <div>
                        <h3 className="text-2xl font-bold text-yellow-600 mb-2">
                          {job.position}
                        </h3>
                        <h4 className="text-xl font-semibold text-stone-200 mb-1">
                          {job.company}
                        </h4>
                        <div className="flex items-center gap-4 text-stone-400 text-sm">
                          <div className="flex items-center gap-1">
                            <Calendar size={16} />
                            <span>{job.period}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin size={16} />
                            <span>{job.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description & Achievements */}
                    <div className="lg:col-span-2 space-y-6">
                      <p className="text-stone-300 text-lg leading-relaxed">
                        {job.description}
                      </p>

                      <div>
                        <h5 className="text-lg font-semibold text-stone-200 mb-4 flex items-center gap-2">
                          <TrendingUp size={20} className="text-yellow-600" />
                          Logros Principales
                        </h5>
                        <div className="grid md:grid-cols-2 gap-3">
                          {job.achievements.map((achievement, achievementIndex) => (
                            <div key={achievementIndex} className="flex items-start gap-3">
                              <div className="w-2 h-2 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                              <p className="text-stone-300 text-sm leading-relaxed">
                                {achievement}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Timeline connector (except for last item) */}
                  {index !== portfolioData.experience.length - 1 && (
                    <div className="mt-8 flex justify-center">
                      <div className="w-px h-8 bg-gradient-to-b from-yellow-600 to-transparent"></div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Career Summary */}
        <div className="mt-16 text-center">
          <Card className="bg-olive-600 border-olive-500 p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Trayectoria de Impacto
            </h3>
            <p className="text-stone-100 text-lg leading-relaxed">
              Desde coordinador en Monsanto hasta gerente de innovación en Clase Azul, 
              mi carrera ha estado marcada por la transformación continua de procesos agrícolas, 
              la implementación de tecnologías disruptivas y el desarrollo de equipos de alto rendimiento 
              que entregan resultados excepcionales en toda la cadena de valor agroalimentaria.
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Experience;